import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { BaseError } from '@utils/types/shared/base';
import { AdminOrganizationsResponse } from '@utils/types/admin';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params';

/**
 * Hook to fetch on-prem organizations list
 *
 * GET /api/admin/on-prem/organizations
 *
 * Returns list of organizations with user counts, session counts, storage usage
 * Supports server-side pagination, sorting, and filtering via URL search params
 */
export const useGetAdminOrganizations = () => {
  const { searchParams } = useNewAppSearchParams();

  const offset = Number.isFinite(Number(searchParams.offset))
    ? Number(searchParams.offset)
    : 0;
  const limit = Number.isFinite(Number(searchParams.limit))
    ? Number(searchParams.limit)
    : 25;

  const sortField = (searchParams.sort as string | undefined) || undefined;
  const sortDirection =
    (searchParams.direction as 'asc' | 'desc' | undefined) || undefined;

  // Extract q parameter (can be string or array)
  const qParam = searchParams.q;
  const qArray = Array.isArray(qParam) ? qParam : qParam ? [qParam] : undefined;

  const fetchOrganizations = async () => {
    const params = new URLSearchParams();
    params.append('offset', String(offset));
    params.append('limit', String(limit));

    if (sortField) {
      params.append('sort', sortField);
    }
    if (sortDirection) {
      params.append('direction', sortDirection);
    }

    // Add all q parameters for filtering
    if (qArray) {
      qArray.forEach(q => {
        params.append('q', q);
      });
    }

    const url = `api/admin/on-prem/organizations?${params.toString()}`;
    const response = await Axios.get<AdminOrganizationsResponse>(url);
    return response.data;
  };

  return useQuery<AdminOrganizationsResponse, AxiosError<BaseError>>(
    ['adminOrganizations', offset, limit, sortField, sortDirection, qArray],
    fetchOrganizations,
    {
      useErrorBoundary: false,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false
    }
  );
};
